/**
 * Custom Reports Page (KAN-87)
 * Template library with My Reports, Shared With Me, and Shared By Admin sections.
 * Includes Run panel with filter controls and download.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../components/auth/AuthContext';
import { customReportService } from '../../services/api/customReportService';
import { dataFeedService } from '../../services/api/dataFeedService';
import CustomReportFilterPanel from '../../components/customReports/CustomReportFilterPanel';
import CustomReportTemplateEditor from '../../components/customReports/CustomReportTemplateEditor';
import ShareDialog from '../../components/customReports/ShareDialog';
import AlertModal from '../../components/common/AlertModal';
import ApiKeyModal from '../../components/common/ApiKeyModal';
import type { ApiKeyDocument } from '../../types/apiKey';
import type {
  AssetClass,
  CustomReportTemplateSummary,
  ClientUser,
  TradeFilters,
  FilterOptions,
  CustomReportGenerateRequest,
} from '../../types/customReports';
import { DEFAULT_TRADE_FILTERS } from '../../types/customReports';
import '../../components/customReports/CustomReports.css';

interface CustomReportsProps {
  assetClass?: AssetClass;
}

const CustomReports: React.FC<CustomReportsProps> = ({ assetClass = 'FX' }) => {
  const { t } = useTranslation();
  const { user } = useAuth();

  const clientId = user?.organization?.id || '';
  const uid = user?.id || '';
  const isAdmin = user?.role === 'client_admin';

  // Template lists
  const [myTemplates, setMyTemplates] = useState<CustomReportTemplateSummary[]>([]);
  const [sharedWithMeTemplates, setSharedWithMeTemplates] = useState<CustomReportTemplateSummary[]>([]);
  const [sharedTemplates, setSharedTemplates] = useState<CustomReportTemplateSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // User map for resolving UIDs to display names
  const [userMap, setUserMap] = useState<Record<string, ClientUser>>({});

  // Search
  const [searchQuery, setSearchQuery] = useState('');

  // Run panel state
  const [runTemplateId, setRunTemplateId] = useState<string | null>(null);
  const [runTemplateScope, setRunTemplateScope] = useState<'USER' | 'SHARED'>('USER');
  const [runTemplateOwnerUid, setRunTemplateOwnerUid] = useState<string | undefined>();
  const [runTemplateName, setRunTemplateName] = useState('');
  const [runSourceType, setRunSourceType] = useState<'TRADES' | 'EVENT_LEVEL_REPORTS'>('TRADES');
  const [runFilters, setRunFilters] = useState<TradeFilters>(DEFAULT_TRADE_FILTERS);
  const [runFilename, setRunFilename] = useState('');
  const [filterOptions, setFilterOptions] = useState<FilterOptions | null>(null);
  const [generating, setGenerating] = useState(false);

  // Alert modal
  const [alertModal, setAlertModal] = useState<{
    isOpen: boolean;
    type: 'success' | 'error' | 'info' | 'warning';
    title: string;
    message: string;
  }>({ isOpen: false, type: 'info', title: '', message: '' });

  // Editor mode
  const [isCreating, setIsCreating] = useState(false);
  const [editingTemplateId, setEditingTemplateId] = useState<string | null>(null);
  const [editingScope, setEditingScope] = useState<'USER' | 'SHARED'>('USER');

  // Confirm delete
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; name: string; scope: 'USER' | 'SHARED'; sharedCount: number } | null>(null);

  // Share dialog
  const [shareTarget, setShareTarget] = useState<{ id: string; name: string; sharedWith: string[] } | null>(null);

  // API Key state (KAN-93)
  const [apiKeyMap, setApiKeyMap] = useState<Record<string, ApiKeyDocument>>({});
  const [apiKeyModal, setApiKeyModal] = useState<{
    isOpen: boolean;
    templateId: string;
    templateName: string;
    existingKey?: ApiKeyDocument | null;
  }>({ isOpen: false, templateId: '', templateName: '' });

  const showError = (message: string) => {
    setAlertModal({ isOpen: true, type: 'error', title: t('common.error', 'Error'), message });
  };

  const showSuccess = (message: string) => {
    setAlertModal({ isOpen: true, type: 'success', title: t('common.success', 'Success'), message });
  };

  // ========== Load Templates ==========

  const loadTemplates = useCallback(async () => {
    if (!clientId || !uid) return;
    setLoading(true);
    setError(null);
    try {
      const promises: [
        Promise<CustomReportTemplateSummary[]>,
        Promise<CustomReportTemplateSummary[]>,
        Promise<CustomReportTemplateSummary[]>,
        Promise<ClientUser[]>,
        Promise<ApiKeyDocument[]> | Promise<never[]>,
      ] = [
        customReportService.listUserTemplates(clientId, uid),
        customReportService.listSharedTemplates(clientId),
        customReportService.listSharedWithMeTemplates(clientId),
        customReportService.getClientUsers(clientId),
        isAdmin ? dataFeedService.listKeys(clientId) : Promise.resolve([]),
      ];
      const [myData, sharedData, sharedWithMeData, clientUsers, apiKeys] = await Promise.all(promises);
      setMyTemplates(myData);
      setSharedTemplates(sharedData);
      setSharedWithMeTemplates(sharedWithMeData);
      // Build UID → user map for resolving owner names
      const map: Record<string, ClientUser> = {};
      clientUsers.forEach(u => { map[u.uid] = u; });
      setUserMap(map);
      // Build templateId → API key map (admin only)
      if (apiKeys.length > 0) {
        const km: Record<string, ApiKeyDocument> = {};
        apiKeys.forEach(k => { km[k.templateId] = k; });
        setApiKeyMap(km);
      }
    } catch (err: any) {
      console.error('Error loading custom report templates:', err);
      setError(err.message || t('customReports.messages.loadError', 'Failed to load templates'));
    } finally {
      setLoading(false);
    }
  }, [clientId, uid]);

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  // ========== Filter Options (loaded once when run panel opens) ==========

  const loadFilterOptions = useCallback(async () => {
    if (!clientId || filterOptions) return;
    try {
      const options = await customReportService.getFilterOptions(clientId, assetClass);
      setFilterOptions(options);
    } catch (err) {
      console.error('Error loading filter options:', err);
    }
  }, [clientId, filterOptions, assetClass]);

  // ========== Handlers ==========

  const handleRun = async (templateId: string, scope: 'USER' | 'SHARED', name: string, savedFilters?: TradeFilters, ownerUid?: string, sourceType?: 'TRADES' | 'EVENT_LEVEL_REPORTS') => {
    let filtersToUse = savedFilters || DEFAULT_TRADE_FILTERS;
    // For event-level templates, pre-fill period to current month if not saved
    if (sourceType === 'EVENT_LEVEL_REPORTS' && !filtersToUse.eventPeriodFrom) {
      const now = new Date();
      const y = now.getFullYear();
      const m = now.getMonth() + 1;
      const monthStr = String(m).padStart(2, '0');
      const lastDay = new Date(y, m, 0).getDate();
      filtersToUse = {
        ...filtersToUse,
        eventPeriodFrom: `${y}-${monthStr}-01`,
        eventPeriodTo: `${y}-${monthStr}-${String(lastDay).padStart(2, '0')}`,
      };
    }
    setRunTemplateId(templateId);
    setRunTemplateScope(scope);
    setRunTemplateOwnerUid(ownerUid);
    setRunTemplateName(name);
    setRunSourceType(sourceType || 'TRADES');
    setRunFilters(filtersToUse);
    setRunFilename('');
    await loadFilterOptions();
  };

  const handleCloseRun = () => {
    setRunTemplateId(null);
  };

  const handleGenerate = async () => {
    if (!runTemplateId || !clientId) return;
    setGenerating(true);
    try {
      const request: CustomReportGenerateRequest = {
        templateId: runTemplateId,
        scope: runTemplateScope,
        ownerUid: runTemplateScope === 'USER' ? (runTemplateOwnerUid || uid) : undefined,
        sourceType: runSourceType,
        assetClass,
        params: { ...runFilters, ...(runFilename.trim() ? { customFilename: runFilename.trim() } : {}) },
      };

      const { blob, filename } = await customReportService.generate(clientId, request);

      // Check for empty file (just headers)
      if (blob.size < 100) {
        showError(t('customReports.messages.noTradesMatch', 'No trades match your filters — try adjusting your filter criteria'));
        return;
      }

      // Trigger download
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err: any) {
      showError(err.message || t('customReports.messages.generateError', 'Report generation failed — please try again'));
    } finally {
      setGenerating(false);
    }
  };

  const handleDuplicate = async (templateId: string, scope: 'USER' | 'SHARED') => {
    try {
      if (scope === 'SHARED') {
        await customReportService.duplicateSharedTemplate(clientId, templateId);
      } else {
        await customReportService.duplicateUserTemplate(clientId, uid, templateId);
      }
      showSuccess(t('customReports.messages.duplicated', 'Template duplicated'));
      await loadTemplates();
    } catch (err: any) {
      showError(err.message || t('customReports.messages.duplicateError', 'Failed to duplicate template'));
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    try {
      if (deleteTarget.scope === 'SHARED') {
        await customReportService.deactivateSharedTemplate(clientId, deleteTarget.id);
      } else {
        await customReportService.deleteUserTemplate(clientId, uid, deleteTarget.id);
      }
      showSuccess(t('customReports.messages.deleted', 'Report deleted'));
      if (runTemplateId === deleteTarget.id) {
        setRunTemplateId(null);
      }
      setDeleteTarget(null);
      await loadTemplates();
    } catch (err: any) {
      showError(err.message || t('customReports.messages.deleteError', 'Failed to delete template'));
    }
  };

  // ========== Editor handlers ==========

  const handleEditorSave = () => {
    setIsCreating(false);
    setEditingTemplateId(null);
    setEditingScope('USER');
    loadTemplates();
  };

  const handleEditorCancel = () => {
    setIsCreating(false);
    setEditingTemplateId(null);
    setEditingScope('USER');
  };

  const handleSaveAsMyOwn = async (templateId: string, ownerUid: string) => {
    try {
      await customReportService.duplicateSharedWithMeTemplate(clientId, templateId, ownerUid);
      showSuccess(t('customReports.messages.savedToCollection', 'Report saved to your collection'));
      await loadTemplates();
    } catch (err: any) {
      showError(err.message || t('customReports.messages.saveError', 'Failed to save report'));
    }
  };

  const handleShareSaved = (sharedWith: string[]) => {
    setShareTarget(null);
    loadTemplates();
    showSuccess(
      sharedWith.length > 0
        ? t('customReports.messages.sharedWith', 'Shared with {{count}} user(s)', { count: sharedWith.length })
        : t('customReports.messages.sharingRemoved', 'Sharing removed')
    );
  };

  // ========== API Key handlers (KAN-93) ==========

  const handleOpenApiKeyModal = (templateId: string, templateName: string) => {
    const existing = apiKeyMap[templateId] || null;
    setApiKeyModal({ isOpen: true, templateId, templateName, existingKey: existing });
  };

  const handleApiKeyChanged = useCallback(async () => {
    // Lightweight refresh — only reload API keys, no loading spinner
    if (!clientId || !isAdmin) return;
    try {
      const apiKeys = await dataFeedService.listKeys(clientId);
      const km: Record<string, ApiKeyDocument> = {};
      apiKeys.forEach(k => { km[k.templateId] = k; });
      setApiKeyMap(km);
    } catch (err) {
      console.error('Error refreshing API keys:', err);
    }
  }, [clientId, isAdmin]);

  // ========== Search filter ==========

  /** Filter templates to only show those matching the current asset class.
   *  Templates without assetClass (pre-KAN-265) are treated as FX. */
  const filterByAssetClass = (templates: CustomReportTemplateSummary[]) => {
    return templates.filter(t => (t.assetClass || 'FX') === assetClass);
  };

  const filterBySearch = (templates: CustomReportTemplateSummary[]) => {
    if (!searchQuery.trim()) return templates;
    const q = searchQuery.toLowerCase();
    return templates.filter(
      t => t.name.toLowerCase().includes(q) || t.description?.toLowerCase().includes(q)
    );
  };

  const filteredMyTemplates = filterBySearch(filterByAssetClass(myTemplates));
  const filteredSharedWithMeTemplates = filterBySearch(filterByAssetClass(sharedWithMeTemplates));
  const filteredSharedTemplates = filterBySearch(filterByAssetClass(sharedTemplates));

  // ========== Render ==========

  const formatDate = (iso?: string) => {
    if (!iso) return '';
    try {
      const d = new Date(iso);
      return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
    } catch {
      return iso;
    }
  };

  const formatDateTime = (iso?: string) => {
    if (!iso) return '-';
    try {
      const d = new Date(iso);
      const day = String(d.getDate()).padStart(2, '0');
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const hours = String(d.getHours()).padStart(2, '0');
      const mins = String(d.getMinutes()).padStart(2, '0');
      return `${day}/${month}/${d.getFullYear()} ${hours}:${mins}`;
    } catch { return iso; }
  };

  const renderTemplateCard = (
    tmpl: CustomReportTemplateSummary,
    _scope: 'USER' | 'SHARED',
    actions: React.ReactNode,
  ) => {
    const isRunOpen = runTemplateId === tmpl.templateId;
    const linkedKey = apiKeyMap[tmpl.templateId];
    return (
      <div key={tmpl.templateId} className="cr-template-card">
        <div className="cr-template-header">
          <div className="cr-template-info">
            {tmpl.isDefault && <span className="cr-default-star" title={t('outputFormats.tooltips.defaultTemplate', 'Default')}>&#9733;</span>}
            <span className="cr-template-name">{tmpl.name}</span>
            <span className="cr-format-badge">{tmpl.outputFormat}</span>
            <span className="cr-template-date">{t('customReports.runPanel.updated', 'Updated {{date}}', { date: formatDate(tmpl.lastUpdatedAt) })}</span>
          </div>
          <div className="cr-template-actions">
            {actions}
          </div>
        </div>
        {/* Second row: description (left) + API key tag (right) */}
        {(tmpl.description || (isAdmin && linkedKey)) && (
          <div className="cr-template-subrow">
            <div className="cr-template-description">
              {tmpl.description || ''}
            </div>
            {isAdmin && linkedKey && (
              <div
                className="cr-api-key-tag"
                onClick={(e) => { e.stopPropagation(); handleOpenApiKeyModal(tmpl.templateId, tmpl.name); }}
                title={t('dataFeeds.actions.manage', 'Manage API Key')}
              >
                <span className="cr-api-key-icon">&#128273;</span>
                <span className="cr-api-key-name">{linkedKey.name}</span>
                <span className={`cr-api-key-status ${linkedKey.isActive ? 'active' : 'disabled'}`}>
                  {linkedKey.isActive
                    ? t('dataFeeds.status.active', 'Active')
                    : t('dataFeeds.status.disabled', 'Disabled')}
                </span>
              </div>
            )}
          </div>
        )}
        {isRunOpen && (
          <div className="cr-run-panel">
            <div className="cr-run-panel-header">
              <span>{t('customReports.runPanel.title', 'Run: {{name}}', { name: runTemplateName })}</span>
              <button className="cr-close-btn" onClick={handleCloseRun}>&times;</button>
            </div>
            <div className="cr-run-filename-row">
              <label>{t('customReports.runPanel.filename', 'Filename')}</label>
              <input
                type="text"
                className="form-input cr-run-filename-input"
                value={runFilename}
                onChange={(e) => setRunFilename(e.target.value)}
                placeholder={runTemplateName.replace(/[^a-zA-Z0-9]/g, '_')}
              />
            </div>
            <CustomReportFilterPanel
              filters={runFilters}
              onChange={setRunFilters}
              filterOptions={filterOptions}
              sourceType={runSourceType}
              assetClass={assetClass}
            />
            <div className="cr-run-panel-footer">
              <button
                className="cr-btn cr-btn-primary"
                onClick={handleGenerate}
                disabled={generating}
              >
                {generating
                  ? t('customReports.buttons.generating', 'Generating...')
                  : t('customReports.buttons.runAndDownload', 'Run & Download {{format}}', { format: tmpl.outputFormat })}
              </button>
            </div>
          </div>
        )}
      </div>
    );
  };

  // ========== Editor mode ==========

  if (isCreating || editingTemplateId) {
    return (
      <CustomReportTemplateEditor
        clientId={clientId}
        uid={uid}
        templateId={editingTemplateId || undefined}
        scope={editingScope}
        assetClass={assetClass}
        onSave={handleEditorSave}
        onCancel={handleEditorCancel}
      />
    );
  }

  // ========== List mode ==========

  if (loading) {
    return (
      <div className="custom-reports-page">
        <div className="cr-loading">
          <div className="spinner" />
          <p>{t('customReports.loading', 'Loading reports...')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="custom-reports-page">
      <div className="cr-toolbar">
        <h2>{t('customReports.pageTitle', 'Custom Reports')}</h2>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button
            className="cr-btn cr-btn-primary"
            onClick={() => { setEditingScope('USER'); setIsCreating(true); }}
          >
            {t('customReports.newReport', '+ New Report')}
          </button>
          {isAdmin && (
            <button
              className="cr-btn cr-btn-primary"
              onClick={() => { setEditingScope('SHARED'); setIsCreating(true); }}
            >
              {t('customReports.newSharedReport', '+ New Shared Report')}
            </button>
          )}
        </div>
      </div>

      {error && <div className="cr-error">{error}</div>}

      <div className="cr-search">
        <input
          type="text"
          placeholder={t('customReports.searchPlaceholder', 'Search reports...')}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="cr-search-input"
        />
      </div>

      {/* My Reports */}
      <div className="cr-section">
        <h3 className="cr-section-title">{t('customReports.sections.myReports', 'MY REPORTS')}</h3>
        {filteredMyTemplates.length === 0 ? (
          <div className="cr-empty-state">
            {myTemplates.length === 0
              ? t('customReports.emptyState.noReports', "You have no saved reports. Click 'New Report' to create your first custom report.")
              : t('customReports.emptyState.noSearchResults', 'No reports match your search.')}
          </div>
        ) : (
          filteredMyTemplates.map(tmpl =>
            renderTemplateCard(tmpl, 'USER', (
              <>
                <button
                  className="cr-btn cr-btn-small"
                  onClick={() => handleRun(tmpl.templateId, 'USER', tmpl.name, tmpl.tradeFilters, undefined, tmpl.sourceType)}
                >
                  {t('customReports.buttons.run', 'Run')}
                </button>
                <button
                  className="cr-btn cr-btn-small"
                  onClick={() => setEditingTemplateId(tmpl.templateId)}
                >
                  {t('customReports.buttons.edit', 'Edit')}
                </button>
                <button
                  className="cr-btn cr-btn-small"
                  onClick={() => setShareTarget({
                    id: tmpl.templateId,
                    name: tmpl.name,
                    sharedWith: tmpl.sharedWith || [],
                  })}
                >
                  {t('customReports.buttons.share', 'Share')}{tmpl.sharedWith && tmpl.sharedWith.length > 0 ? ` (${tmpl.sharedWith.length})` : ''}
                </button>
                <button
                  className="cr-btn cr-btn-small"
                  onClick={() => handleDuplicate(tmpl.templateId, 'USER')}
                >
                  {t('customReports.buttons.duplicate', 'Duplicate')}
                </button>
                <button
                  className="cr-btn cr-btn-small cr-btn-danger"
                  onClick={() => setDeleteTarget({
                    id: tmpl.templateId,
                    name: tmpl.name,
                    scope: 'USER',
                    sharedCount: tmpl.sharedWith?.length || 0,
                  })}
                >
                  {t('customReports.buttons.delete', 'Delete')}
                </button>
              </>
            ))
          )
        )}
      </div>

      {/* Shared With Me */}
      {filteredSharedWithMeTemplates.length > 0 && (
        <div className="cr-section">
          <h3 className="cr-section-title">{t('customReports.sections.sharedWithMe', 'SHARED WITH ME')}</h3>
          {filteredSharedWithMeTemplates.map(tmpl => {
            const ownerName = tmpl.ownerUid ? (userMap[tmpl.ownerUid]?.displayName || 'Unknown') : 'Unknown';
            return renderTemplateCard(
              { ...tmpl, description: tmpl.description || t('customReports.filters.sharedBy', 'Shared by {{name}}', { name: ownerName }) },
              'USER',
              (
                <>
                  <button
                    className="cr-btn cr-btn-small"
                    onClick={() => handleRun(tmpl.templateId, 'USER', tmpl.name, tmpl.tradeFilters, tmpl.ownerUid, tmpl.sourceType)}
                  >
                    {t('customReports.buttons.run', 'Run')}
                  </button>
                  <button
                    className="cr-btn cr-btn-small"
                    onClick={() => handleSaveAsMyOwn(tmpl.templateId, tmpl.ownerUid || '')}
                    title={t('customReports.buttons.saveAsMyOwnTooltip', 'Save a copy to My Reports')}
                  >
                    {t('customReports.buttons.saveAsMyOwn', 'Save As My Own')}
                  </button>
                </>
              ),
            );
          })}
        </div>
      )}

      {/* Shared By Admin */}
      {filteredSharedTemplates.length > 0 && (
        <div className="cr-section">
          <h3 className="cr-section-title">{t('customReports.sections.sharedByAdmin', 'SHARED BY ADMIN')}</h3>
          {filteredSharedTemplates.map(tmpl =>
            renderTemplateCard(tmpl, 'SHARED', (
              <>
                <button
                  className="cr-btn cr-btn-small"
                  onClick={() => handleRun(tmpl.templateId, 'SHARED', tmpl.name, tmpl.tradeFilters, undefined, tmpl.sourceType)}
                >
                  {t('customReports.buttons.run', 'Run')}
                </button>
                <button
                  className="cr-btn cr-btn-small"
                  onClick={() => handleDuplicate(tmpl.templateId, 'SHARED')}
                >
                  {t('customReports.buttons.duplicate', 'Duplicate')}
                </button>
                {isAdmin && (
                  <>
                    <button
                      className="cr-btn cr-btn-small"
                      onClick={() => { setEditingScope('SHARED'); setEditingTemplateId(tmpl.templateId); }}
                    >
                      {t('customReports.buttons.edit', 'Edit')}
                    </button>
                    <button
                      className="cr-btn cr-btn-small cr-btn-danger"
                      onClick={() => setDeleteTarget({
                        id: tmpl.templateId,
                        name: tmpl.name,
                        scope: 'SHARED',
                        sharedCount: 0,
                      })}
                    >
                      {t('customReports.buttons.delete', 'Delete')}
                    </button>
                    {!apiKeyMap[tmpl.templateId] && (
                      <button
                        className="cr-btn cr-btn-small"
                        onClick={() => handleOpenApiKeyModal(tmpl.templateId, tmpl.name)}
                      >
                        {t('dataFeeds.actions.generateKey', 'API Key')} &#128273;
                      </button>
                    )}
                  </>
                )}
              </>
            ))
          )}
        </div>
      )}

      {/* Share Dialog */}
      {shareTarget && (
        <ShareDialog
          isOpen={true}
          clientId={clientId}
          uid={uid}
          templateId={shareTarget.id}
          templateName={shareTarget.name}
          currentSharedWith={shareTarget.sharedWith}
          onClose={() => setShareTarget(null)}
          onSaved={handleShareSaved}
        />
      )}

      {/* Delete Confirmation Modal */}
      {deleteTarget && (
        <AlertModal
          isOpen={true}
          type="warning"
          title={t('customReports.deleteModal.title', 'Delete Report')}
          message={
            deleteTarget.sharedCount > 0
              ? t('customReports.deleteModal.confirmShared', 'Are you sure you want to delete "{{name}}"? This report is shared with {{count}} user(s). They will lose access. This cannot be undone.', { name: deleteTarget.name, count: deleteTarget.sharedCount })
              : t('customReports.deleteModal.confirm', 'Are you sure you want to delete "{{name}}"? This cannot be undone.', { name: deleteTarget.name })
          }
          onClose={() => setDeleteTarget(null)}
          onConfirm={handleDeleteConfirm}
          confirmLabel={t('customReports.buttons.delete', 'Delete')}
        />
      )}

      {/* Alert Modal */}
      <AlertModal
        isOpen={alertModal.isOpen}
        type={alertModal.type}
        title={alertModal.title}
        message={alertModal.message}
        onClose={() => setAlertModal(prev => ({ ...prev, isOpen: false }))}
      />

      {/* API Key Modal (KAN-93) */}
      <ApiKeyModal
        isOpen={apiKeyModal.isOpen}
        onClose={() => setApiKeyModal(prev => ({ ...prev, isOpen: false }))}
        clientId={clientId}
        templateId={apiKeyModal.templateId}
        templateName={apiKeyModal.templateName}
        templateSource="CUSTOM_REPORT"
        existingKey={apiKeyModal.existingKey}
        onKeyChanged={handleApiKeyChanged}
      />
    </div>
  );
};

export default CustomReports;
